import * as React from 'react'
import { Check, Copy } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getJSON, postJSON, deleteJSON } from '@/lib/api'

type ScannerLinkState = 'absent' | 'active' | 'revoked' | 'expired'

export type ScannerLinkStatus = {
  state: ScannerLinkState
  token?: string | null
  expires_at?: string
  created_at?: string
  created_by?: string
  revoked_at?: string | null
  revoked_by?: string | null
}

export function EventScannerLinkCard({ eventId }: { eventId: number }) {
  const queryClient = useQueryClient()
  const [confirmingRevoke, setConfirmingRevoke] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const statusQuery = useQuery({
    queryKey: ['event-scanner-link', eventId],
    queryFn: () =>
      getJSON<{ scanner_link: ScannerLinkStatus }>(`/api/v1/events/${eventId}/scanner_link`),
    retry: false,
  })
  const create = useMutation({
    mutationFn: () => postJSON(`/api/v1/events/${eventId}/scanner_link`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event-scanner-link', eventId] }),
  })
  const revoke = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/events/${eventId}/scanner_link`),
    onSuccess: () => {
      setConfirmingRevoke(false)
      queryClient.invalidateQueries({ queryKey: ['event-scanner-link', eventId] })
    },
  })

  if (statusQuery.isLoading || statusQuery.isError) return null
  const status = statusQuery.data?.scanner_link
  if (!status) return null
  const link = status.token ? scannerLinkUrl(status.token) : null

  async function copyLink() {
    if (!link) return
    await navigator.clipboard.writeText(link)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1_500)
  }

  return (
    <>
      <section className="mx-auto w-full max-w-[390px] rounded-md border border-border bg-background p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-body font-medium text-foreground">Door scanner link</h2>
          {status.state === 'active' && <Badge variant="success">Active</Badge>}
        </div>

        {status.state === 'absent' && (
          <div className="mt-3 space-y-3">
            <p className="text-body-sm text-muted-foreground">
              Give door volunteers the scanner and door list without an account.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              Create scanner link
            </Button>
          </div>
        )}

        {status.state === 'active' && link && (
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-1">
              <span className="truncate text-body-sm text-foreground">{link}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                aria-label="Copy link"
                onClick={copyLink}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="space-y-0.5">
              <p className="text-caption text-muted-foreground">
                {expiryCaption(status.expires_at)}
              </p>
              <p className="text-caption text-muted-foreground">
                Created by {status.created_by} · {dateCaption(status.created_at)}
              </p>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-caption text-muted-foreground">
                Revoking signs every phone out immediately.
              </p>
              <Button
                variant="quietDestructive"
                size="sm"
                onClick={() => setConfirmingRevoke(true)}
              >
                Revoke link
              </Button>
            </div>
          </div>
        )}

        {status.state === 'revoked' && (
          <SpentState
            text={`Link revoked ${dateCaption(status.revoked_at)} by ${status.revoked_by}.`}
            onCreate={() => create.mutate()}
            creating={create.isPending}
          />
        )}
        {status.state === 'expired' && (
          <SpentState
            text={`Link expired ${dateCaption(status.expires_at)} — 24 hours after the event ended.`}
            onCreate={() => create.mutate()}
            creating={create.isPending}
          />
        )}
      </section>

      <RevokeDialog
        open={confirmingRevoke}
        pending={revoke.isPending}
        onOpenChange={setConfirmingRevoke}
        onConfirm={() => revoke.mutate()}
      />
    </>
  )
}

function RevokeDialog({
  open,
  pending,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  pending: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke scanner link?</DialogTitle>
          <DialogDescription>
            Every phone using this link will be signed out immediately.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Keep link
          </Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} disabled={pending}>
            Revoke link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SpentState({
  text,
  onCreate,
  creating,
}: {
  text: string
  onCreate: () => void
  creating: boolean
}) {
  return (
    <div className="mt-3 space-y-3">
      <p className="text-body-sm text-muted-foreground">{text}</p>
      <Button variant="outline" size="sm" onClick={onCreate} disabled={creating}>
        Create new link
      </Button>
    </div>
  )
}

function scannerLinkUrl(token: string) {
  return `${window.location.origin}/api/v1/scanner_links/${token}`
}

function expiryCaption(value?: string) {
  return `Expires ${dateCaption(value)} — 24 hours after the event ends`
}

function dateCaption(value?: string | null) {
  return value
    ? new Date(value).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—'
}
